import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { BoReference } from '../../bos/bo-reference.js'
import { Expression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { BlockWithLink } from './block-with-link.js'
import { assignCommonProperties } from '../../util/data/assign-common-properties.js'

export const LinkTargetTypes = {
	screen: 'Screen',
	url: 'URL',
	none: 'None (no link)',
} as const

export const LinkOpenIns = {
	_self: 'In same frame (default, "_self")',
	_blank: 'In new window/tab ("_blank")',
	_top: 'Replace whole window ("_top")',
	_parent: 'Replace parent frame ("_parent")',
	named: 'In explicitly named window',
} as const

@jsonClass('BlockLinkDefinition')
export class BlockLinkDefinition {
	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 1,
		label: 'Link target type',
		options: Object.entries(LinkTargetTypes),
	})
	targetType: keyof typeof LinkTargetTypes = 'screen'

	@jsonProperty(BoReference)
	@ScreenEditorInput({
		inputType: 'boRef',
		order: 2,
		label: 'Link to Screen',
		allowedBoTypes: ['Screen'],
		includeImportedModules: true,
		isVisible: (items: BlockWithLink[]) => items[0].link.targetType == 'screen',
		isMandatory: () => true,
	})
	screenRef: BoReference = new BoReference()

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 3,
		label: 'Screen URL parameters',
		codeLanguage: 'ts',
		tsReturnType: () => 'Record<string, number | string>',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (items: BlockWithLink[]) => items[0].link.targetType == 'screen',
		isMandatory: () => true,
	})
	screenParamsCode = new Expression('{}')

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Link URL',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (items: BlockWithLink[]) => items[0].link.targetType == 'url',
		isMandatory: () => true,
	})
	url = new TextOrExpression_assumeExpression('', 'T')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 3,
		label: 'Open link in',
		options: Object.entries(LinkOpenIns),
		isVisible: (items: BlockWithLink[]) => items[0].link.targetType != 'none',
	})
	openIn: keyof typeof LinkOpenIns = '_self'

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Open link in window name',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (items: BlockWithLink[]) =>
			items[0].link.targetType != 'none' && items[0].link.openIn == 'named',
		isMandatory: () => true,
	})
	openInWindowNameCode = new TextOrExpression_assumeExpression('', 'T')

	@jsonProperty()
	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 3,
		label: 'Browser should download link',
		isBinding: false,
	})
	shouldDownload: boolean = false

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput<BlockWithLink>({
		inputType: 'code',
		order: 4,
		label: 'Download file name',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (items) => items[0].link.shouldDownload,
		leaveGapIfHidden: () => true,
		isMandatory: () => true,
	})
	downloadFileNameCode = new TextOrExpression_assumeExpression('', 'T')

	constructor(init?: Partial<BlockLinkDefinition>) {
		assignCommonProperties(this, init)
	}
}
